import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import apolloClient from '../../../graphql/apollo-client';
import { GET_CLIENT_BRANDS } from '../../../graphql/queries';
import { CREATE_CLIENT_BRAND, UPDATE_CLIENT_BRAND, DELETE_CLIENT_BRAND } from '../../../graphql/mutations';
import { useAuthStore } from '../../../store/authStore';
import { INTEREST_CATEGORIES } from '../../../constants/categories';
import { COUNTRIES } from '../../../constants/countries';
import AddressPicker, {
  AddressPickerValue,
  StructuredExtras,
} from '../../common/AddressPicker';

interface ClientBrand {
  brandId: string;
  userId: string;
  brandName: string;
  industry: string;
  country: string;
  city?: string;
  location: string;
  latitude?: number | null;
  longitude?: number | null;
  website?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  campaignCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface BrandFormData {
  brandName: string;
  industry: string;
  country: string;
  city: string;
  location: string;
  latitude: number | null;
  longitude: number | null;
  website: string;
  contactPerson: string;
  contactEmail: string;
  contactPhone: string;
}

const EMPTY_FORM: BrandFormData = {
  brandName: '', industry: '', country: '', city: '', location: '',
  latitude: null, longitude: null,
  website: '', contactPerson: '', contactEmail: '', contactPhone: '',
};

const ClientBrandsPage: React.FC = () => {
  const { t } = useTranslation(['profile', 'common']);
  const { user } = useAuthStore();
  const [search, setSearch] = useState('');
  const [brands, setBrands] = useState<ClientBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<ClientBrand | null>(null);
  const [form, setForm] = useState<BrandFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchBrands = useCallback(async () => {
    if (!user?.userId) return;
    try {
      setLoading(true);
      const res = await apolloClient.query({
        query: gql`${GET_CLIENT_BRANDS}`,
        variables: { userId: user.userId },
        fetchPolicy: 'network-only',
      });
      const result = res.data?.getClientBrands;
      if (result?.success) setBrands(result.brands || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const filtered = search
    ? brands.filter(b => b.brandName.toLowerCase().includes(search.toLowerCase()))
    : brands;

  const openAddModal = () => {
    setEditingBrand(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (brand: ClientBrand) => {
    setEditingBrand(brand);
    setForm({
      brandName: brand.brandName, industry: brand.industry, country: brand.country,
      city: brand.city || '',
      location: brand.location,
      latitude: brand.latitude ?? null, longitude: brand.longitude ?? null,
      website: brand.website || '', contactPerson: brand.contactPerson || '',
      contactEmail: brand.contactEmail || '', contactPhone: brand.contactPhone || '',
    });
    setShowModal(true);
  };

  const handleAddressPickerChange = useCallback(
    (next: AddressPickerValue, extras?: StructuredExtras) => {
      setForm(prev => {
        const merged: BrandFormData = {
          ...prev,
          location: next.text,
          latitude: next.latitude,
          longitude: next.longitude,
        };
        // Country and city are read-only and only populated from the
        // resolved address picker (Google Places extras). Mirror them
        // here so the user never has to type them manually.
        if (extras) {
          if (extras.country) {
            const code = extras.country.toLowerCase();
            if ((COUNTRIES as readonly string[]).includes(code)) {
              merged.country = code;
            }
          }
          if (extras.city) {
            merged.city = extras.city;
          }
        }
        return merged;
      });
    },
    [],
  );

  const handleSave = async () => {
    if (!form.brandName || !form.industry || !form.country || !form.location) return;
    setSaving(true);
    try {
      const { latitude, longitude, ...rest } = form;
      const basePayload: Record<string, unknown> = { ...rest };
      if (latitude !== null || longitude !== null) {
        basePayload.latitude = latitude;
        basePayload.longitude = longitude;
      }
      if (editingBrand) {
        await apolloClient.mutate({
          mutation: gql`${UPDATE_CLIENT_BRAND}`,
          variables: { input: { brandId: editingBrand.brandId, ...basePayload } },
        });
      } else {
        await apolloClient.mutate({
          mutation: gql`${CREATE_CLIENT_BRAND}`,
          variables: { input: basePayload },
        });
      }
      setShowModal(false);
      fetchBrands();
    } catch (err) {
      console.error('Failed to save brand:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (brandId: string) => {
    if (!window.confirm(t('profile:myProfile.clientBrands.confirmDelete'))) return;
    try {
      await apolloClient.mutate({
        mutation: gql`${DELETE_CLIENT_BRAND}`,
        variables: { brandId },
      });
      fetchBrands();
    } catch (err) {
      console.error('Failed to delete brand:', err);
    }
  };

  const isFormValid = form.brandName && form.industry && form.country && form.location;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-xl font-bold text-gray-900 mb-2">
        {t('profile:myProfile.clientBrands.title')}
      </h1>
      <hr className="border-gray-200 mb-6" />

      <div className="flex items-center mb-6 gap-3">
        <button type="button" onClick={openAddModal}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 whitespace-nowrap">
          {t('profile:myProfile.clientBrands.addBrand')}
        </button>
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t('profile:myProfile.clientBrands.searchPlaceholder')}
          className="w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
        <button type="button"
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap">
          {t('common:search')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3 font-medium">{t('profile:myProfile.clientBrands.country')}</th>
              <th className="px-4 py-3 font-medium">{t('common:visitCity')}</th>
              <th className="px-4 py-3 font-medium">{t('profile:myProfile.clientBrands.brandName')}</th>
              <th className="px-4 py-3 font-medium">{t('profile:myProfile.clientBrands.industry')}</th>
              <th className="px-4 py-3 font-medium">{t('profile:myProfile.clientBrands.campaignCount')}</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{t('profile:myProfile.clientBrands.empty')}</td></tr>
            ) : filtered.map((brand) => (
              <tr key={brand.brandId} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3">{t(`common:countries.${brand.country}`, brand.country)}</td>
                <td className="px-4 py-3">{brand.city || '-'}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{brand.brandName}</td>
                <td className="px-4 py-3">{t(`common:categories.${brand.industry}`, brand.industry)}</td>
                <td className="px-4 py-3">{brand.campaignCount || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEditModal(brand)}
                      className="px-3 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded hover:bg-blue-50">Edit</button>
                    <button onClick={() => handleDelete(brand.brandId)}
                      className="px-3 py-1 text-xs font-medium text-red-600 border border-red-300 rounded hover:bg-red-50">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Brand Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">
                {editingBrand ? t('profile:myProfile.clientBrands.editBrand') : t('profile:myProfile.clientBrands.brandInfo')}
              </h2>
            </div>

            <div className="px-6 py-4 space-y-5">
              {/* 필수정보 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('profile:myProfile.clientBrands.requiredInfo')}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile:myProfile.clientBrands.brandNameLabel')} <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={form.brandName} onChange={e => setForm({...form, brandName: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile:myProfile.clientBrands.industryLabel')} <span className="text-red-500">*</span>
                    </label>
                    <select value={form.industry} onChange={e => setForm({...form, industry: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500">
                      <option value="">{t('common:select')}</option>
                      {INTEREST_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{t(`common:categories.${cat}`)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile:myProfile.clientBrands.countryLabel')} <span className="text-red-500">*</span>
                    </label>
                    {/*
                      Read-only: country is set automatically from the
                      resolved Google Places address. Manual override is
                      disabled to keep country in sync with the actual
                      location.
                    */}
                    <select value={form.country} disabled aria-readonly="true"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed">
                      <option value="">{t('common:select')}</option>
                      {COUNTRIES.map(c => (
                        <option key={c} value={c}>{t(`common:countries.${c}`)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('common:visitCity')}
                    </label>
                    <input type="text" value={form.city} readOnly aria-readonly="true"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile:myProfile.clientBrands.locationLabel')} <span className="text-red-500">*</span>
                    </label>
                    <AddressPicker
                      mode="single"
                      value={{
                        text: form.location,
                        latitude: form.latitude,
                        longitude: form.longitude,
                      }}
                      onChange={handleAddressPickerChange}
                    />
                  </div>
                </div>
              </div>

              {/* 추가 정보 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('profile:myProfile.clientBrands.additionalInfo')}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.websiteLabel')}</label>
                    <input type="text" value={form.website} onChange={e => setForm({...form, website: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.contactPersonLabel')}</label>
                    <input type="text" value={form.contactPerson} onChange={e => setForm({...form, contactPerson: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.contactEmailLabel')}</label>
                    <input type="email" value={form.contactEmail} onChange={e => setForm({...form, contactEmail: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.contactPhoneLabel')}</label>
                    <input type="tel" value={form.contactPhone} onChange={e => setForm({...form, contactPhone: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                {t('common:cancel')}
              </button>
              <button type="button" onClick={handleSave} disabled={!isFormValid || saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? t('common:saving') : t('common:save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientBrandsPage;
